import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { formatDistanceToNow } from 'date-fns'
import { Search, Phone, MessageSquare, Calendar, User, UserPlus, Trash2 } from 'lucide-react'
import { leadAPI, profileAPI } from '../../../services/api'
import toast from 'react-hot-toast'

const LeadsPage = () => {
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    fetchLeads()
  }, [])

  const fetchLeads = async () => {
    try {
      setLoading(true)
      // 1. Get My Profile ID
      const profileRes = await profileAPI.getMyProfile()
      if (!profileRes.data.success || !profileRes.data.data) {
        setLoading(false)
        return
      }
      
      const profileId = profileRes.data.data._id

      // 2. Fetch Leads
      const leadsRes = await leadAPI.getLeadsByProfile(profileId)
      if (leadsRes.data.success) {
        setLeads(leadsRes.data.data)
      }
    } catch (error) {
      console.error(error)
      toast.error('Failed to load inquiries')
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return
    
    try {
        await leadAPI.deleteLead(id)
        toast.success('Inquiry deleted')
        setLeads(leads.filter(l => l._id !== id))
    } catch (error) {
        console.error(error)
        toast.error('Failed to delete')
    }
  }

  const filteredLeads = leads.filter(lead => 
    lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.phone.includes(searchTerm)
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <UserPlus className="w-8 h-8 text-primary-500" />
            Profile Inquiries
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            View everyone who has unlocked and viewed your profile.
          </p>
        </div>
        
        {/* Search */}
        <div className="relative">
          <input 
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 w-full md:w-64"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-gray-500 dark:text-gray-400 text-sm font-medium uppercase">Total Inquiries</h3>
            <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{leads.length}</p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
            <h3 className="text-gray-500 dark:text-gray-400 text-sm font-medium uppercase">Last 7 Days</h3>
            <p className="text-3xl font-bold text-primary-500 mt-2">
                {leads.filter(l => new Date(l.createdAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
            </p>
        </div>
      </div>

      {/* Leads List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        {loading ? (
            <div className="p-8 text-center text-gray-500">Loading inquiries...</div>
        ) : filteredLeads.length === 0 ? (
            <div className="p-12 text-center">
                <div className="bg-gray-100 dark:bg-gray-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UserPlus className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">No inquiries yet</h3>
                <p className="text-gray-500 dark:text-gray-400 mt-1">When someone unlocks your profile, they will appear here.</p>
            </div>
        ) : (
            <div className="max-h-[600px] overflow-y-auto">
                <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 z-10">
                        <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                            <th className="p-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Visitor Name</th>
                            <th className="p-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Contact</th>
                            <th className="p-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Query / Message</th>
                            <th className="p-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                            <th className="p-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {filteredLeads.map((lead, idx) => (
                            <motion.tr 
                                key={lead._id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.05 }}
                                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                            >
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900 dark:to-accent-900 flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold">
                                            {lead.name.charAt(0)}
                                        </div>
                                        <div className="font-medium text-gray-900 dark:text-white">
                                            {lead.name}
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4">
                                    <div className="space-y-1">
                                        <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-primary-500 transition-colors">
                                            <Phone className="w-4 h-4" />
                                            {lead.phone}
                                        </a>
                                        {lead.email && (
                                             <div className="text-xs text-gray-500">{lead.email}</div>
                                        )}
                                    </div>
                                </td>
                                <td className="p-4">
                                    <div className="flex items-start gap-2 max-w-sm">
                                        <MessageSquare className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                                        <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2" title={lead.message}>
                                            {lead.message || <span className="italic text-gray-400">No message provided</span>}
                                        </p>
                                    </div>
                                </td>
                                <td className="p-4 whitespace-nowrap">
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <Calendar className="w-4 h-4" />
                                        {formatDistanceToNow(new Date(lead.createdAt), { addSuffix: true })}
                                    </div>
                                </td>
                                <td className="p-4">
                                    <button 
                                        onClick={() => handleDelete(lead._id)}
                                        className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                        title="Delete Inquiry"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </motion.tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
      </div>
    </div>
  )
}

export default LeadsPage
